import { IS_INTERNAL_ENV } from "constants/app.constants";
import { DEFAULT_FONT_SIZE } from "constants/chart.constants";
import {
  HOR_CUTOFF_ANGLE_DEG,
  VERT_CUTOFF_ANGLE_DEG
} from "constants/octantZone.constants";
import {
  INITIAL_SCALE_BY_VALUE,
  scaleByOptions
} from "constants/visualization.constants";
import _cloneDeep from "lodash/cloneDeep";
import { withDefaults } from "stores/utils/withDefaults";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { makeIpdbDisplayDefaults } from "models/ipdbDisplaySettings";

import { XDASettingsT, XDA_RADIUS_TYPES } from "./types";

// ─── Constants ───────────────────────────────────────────────────────────────
export { INITIAL_SCALE_BY_VALUE };
export const DEFAULT_TOPS_MODEL_SOURCE: readonly string[] = ["McDaniel"];
export const DEFAULT_IPDB_SOURCE = "McDaniel";

// ─── State ───────────────────────────────────────────────────────────────────
// Flattened `XDASettingsT` — the `{xda:{settings}}` nesting used by the
// replaced Context-based implementation only existed as an artifact of its
// generic reducer `Action` shape. `showScreenshot` is intentionally dropped
// (confirmed dead — no reads/writes anywhere in the codebase).
export type XdaSettingsState = XDASettingsT;

// Resolved cross-section dimensions (in meters) for a radius preset — sourced
// either from org-defined play defaults (`org_<section>`) or a geo/stick
// calculation job (job name). The component keeps ownership of preset lookup
// (React Query data); the store only applies the resolved dims.
export interface RadiusPresetDims {
  readonly up: number;
  readonly down: number;
  readonly left: number;
  readonly right: number;
}

// ─── Actions ─────────────────────────────────────────────────────────────────
export interface XdaSettingsActions {
  // Full-replace action — matches the settings-panel call sites (XdaDataOptions,
  // XdaGridOptions, XdaSettingsOptions, Xda3DGeoOptions, XdaResetSettings) that
  // mount only inside popovers and already spread current settings + overrides.
  readonly setXdaSettings: (next: XDASettingsT) => void;
  // Patch action — for always-mounted toolbar toggles that only touch 1-2
  // fields (code review: without this, every such toggle subscribes via
  // useShallow(selectXdaSettings) to the full ~40-field object and re-renders
  // on any field change, same re-render-everything behavior as the old
  // Context, just relocated). Consumers should pair this with a scalar
  // selector from xdaSettingsSelectors.ts, not the full-state selector.
  readonly updateXdaSettings: (patch: Partial<XDASettingsT>) => void;
  // Named domain event (code review: replaces the two near-identical inline
  // { ...settings, ... } + setXdaSettings blocks in
  // XdaRadiusOptions.handleRadiusChange for the org/job branches) — sets
  // selectedRadius plus the four dimension fields in one state transition.
  // No unit conversion here: preset dims are already in meters, applied
  // verbatim, matching the replaced inline blocks exactly.
  readonly applyRadiusPreset: (preset: {
    selectedRadius: string;
    dims: RadiusPresetDims;
  }) => void;
  readonly reset: () => void;
}

// ─── Combined store type ─────────────────────────────────────────────────────
export type XdaSettingsStore = XdaSettingsState & XdaSettingsActions;

// ─── Initial state factory ───────────────────────────────────────────────────
const makeXdaSettingsDefaults = (): XdaSettingsState => ({
  ...makeIpdbDisplayDefaults(),
  activeXdaPlay: undefined,
  bin: null,
  completionLength: 500,
  dataFields: [],
  downHeight: 0,
  leftWidth: 0,
  upHeight: 0,
  rightWidth: 0,
  hangWellsToTop: true,
  isUsingResourcePlayDefault: false,
  ipdbColor: "rainbow",
  ipdbField: "Porosity",
  ipdbUnit: "(%)",
  ipdbSource: DEFAULT_IPDB_SOURCE,
  ipdbTitleSize: DEFAULT_FONT_SIZE,
  ipdbFontSize: DEFAULT_FONT_SIZE,
  ipdbOpacity: 1,
  topsModelSource: [...DEFAULT_TOPS_MODEL_SOURCE],
  scaleByOption: scaleByOptions[0],
  scaleByValue: INITIAL_SCALE_BY_VALUE,
  selectedPlays: [],
  showCompletion: false,
  showData: false,
  showGrid: false,
  showIpdbLegend: IS_INTERNAL_ENV,
  showMeasurement: false,
  showTooltip: true,
  showXdaValueInfo: false,
  showAllTops: false,
  showOverlap: false,
  showDucWells: false,
  showRelativeDepth: false,
  showTVDSSDepth: false,
  selectedRadius: XDA_RADIUS_TYPES.Gross,
  widthScaled: 1,
  resetZuiInstance: false,
  includeZero: true,
  // EVA-163 — Spacing Class controls. The two band sizes are deliberately
  // absent rather than seeded: an empty override map resolves to whichever
  // unit system's canonical pair is in force at draw time, which is the EVA-29
  // behaviour. Seeding one system's numbers here would freeze the widget to
  // that system the moment it was created.
  spacingClassFilter: [],
  octantLengthOverrides: {},
  octantHorCutoffDeg: HOR_CUTOFF_ANGLE_DEG,
  octantVertCutoffDeg: VERT_CUTOFF_ANGLE_DEG
});

export const createInitialState = withDefaults(makeXdaSettingsDefaults);

// ─── Store factory ───────────────────────────────────────────────────────────
// `seed` is used verbatim as the store's starting data — it is NOT merged
// with factory defaults here (unlike its sibling stores' `overrides?: Partial<...>`
// param, hence the different name — code review: the two contracts looked
// identical at the call site despite behaving differently). This matters when
// a caller (the Provider) seeds the store from a possibly-incomplete
// saved/session-stripped settings object: VisContext's original behavior used
// that object as-is (`{...initialState}`), so a field genuinely absent from a
// saved dashboard's settings stayed absent rather than silently reappearing
// via a factory default. Callers that want factory defaults should build
// `seed` via `createInitialState(...)` first (this is what happens for a
// brand-new widget with no saved settings).
export const createXdaSettingsStore = (seed: XdaSettingsState = createInitialState()) => {
  return createStore<XdaSettingsStore>()(
    subscribeWithSelector((set) => ({
      ...seed,

      setXdaSettings: (next) => set(next),

      updateXdaSettings: (patch) => set(patch),

      applyRadiusPreset: ({ selectedRadius, dims }) =>
        set({
          selectedRadius,
          upHeight: dims.up,
          downHeight: dims.down,
          leftWidth: dims.left,
          rightWidth: dims.right
        }),

      // cloneDeep, not `set(seed)`: `seed`'s arrays (dataFields,
      // topsModelSource, selectedPlays) are the same references on every
      // reset() call otherwise — sibling stores avoid this by calling
      // createInitialState(overrides) fresh each time, which this store
      // can't do (seed is used verbatim, not merged with defaults), so
      // reset() clones instead (code review).
      reset: () => set(_cloneDeep(seed))
    }))
  );
};

export type XdaSettingsStoreApi = ReturnType<typeof createXdaSettingsStore>;
